import React, { useEffect, useRef } from "react";
import { Box, Card, Typography, Grid, Chip } from "@mui/material";
import * as echarts from "echarts";

// Utils
import { bytesToGB } from 'utils/format';

// Const
import { CHART_TOOLTIP_STYLE } from "../const";


// Strips any leading sign/arrow characters (handles backend values with
// duplicated signs, e.g. "--2") to get the plain magnitude, e.g. "2".
const stripRankChangeSign = (valStr) => valStr.replace(/^[+\-▲▼]+/, "");

// Normalizes a raw rankBadge value (e.g. "--2", "▲2", "NEW", "=") into
// display text for the tooltip, mirroring TrendBadge's parsing rules.
const formatRankBadgeDisplay = (rawValue) => {
  if (rawValue === null || rawValue === undefined || rawValue === "") return null;

  const valStr = String(rawValue).trim();
  const changeStr = valStr.toUpperCase();

  if (changeStr === "NEW") return "NEW";
  if (valStr === "0" || valStr === "=") return "0";

  if (valStr.startsWith("+") || valStr.startsWith("▲") || Number(valStr) > 0) {
    return `+${stripRankChangeSign(valStr)}`;
  }

  if (valStr.startsWith("-") || valStr.startsWith("▼") || Number(valStr) < 0) {
    return `-${stripRankChangeSign(valStr)}`;
  }

  return valStr;
};

const TrendBadge = ({ rankChange }) => {
  if (!rankChange && rankChange !== 0) return null;

  const valStr = String(rankChange).trim();
  const changeStr = valStr.toUpperCase();

  if (changeStr === "NEW") {
    return (
      <Chip
        label="NEW"
        size="small"
        sx={{
          height: "18px",
          fontSize: "10px",
          fontWeight: "bold",
          bgcolor: "#707ca8",
          color: "#fff",
          "& .MuiChip-label": {
            px: 0.75,
          },
        }}
      />
    );
  }

  // Parse positive changes (+1, +2, ▲1)
  if (valStr.startsWith("+") || valStr.startsWith("▲") || (Number(valStr) > 0)) {
    const num = stripRankChangeSign(valStr);
    return (
      <Typography
        component="span"
        sx={{
          fontSize: "11px",
          color: "#2baf4a",
          fontWeight: "bold",
          display: "inline-flex",
          alignItems: "center",
        }}
      >
        ▲{num}
      </Typography>
    );
  }

  // Parse negative changes (-1, -2, ▼1)
  if (valStr.startsWith("-") || valStr.startsWith("▼") || (Number(valStr) < 0)) {
    const num = stripRankChangeSign(valStr);
    return (
      <Typography
        component="span"
        sx={{
          fontSize: "11px",
          color: "#f75555",
          fontWeight: "bold",
          display: "inline-flex",
          alignItems: "center",
        }}
      >
        ▼{num}
      </Typography>
    );
  }

  // Parse no change (0, =)
  if (valStr === "0" || valStr === "=") {
    return (
      <Box
        sx={{
          width: "10px",
          height: "3px",
          backgroundColor: "#707ca8",
        }}
      >
      </Box>
    );
  }

  // Fallback
  return (
    <Typography
      component="span"
      sx={{
        fontSize: "11px",
        color: "#707ca8",
        fontWeight: "bold",
      }}
    >
      {valStr}
    </Typography>
  );
};

const SingleRankChart = ({ title, data = [], valueSuffix = "" }) => {
  const chartRef = useRef(null);
  const chartInstance = useRef(null);
  const reversedData = [...data].reverse();

  const isTraffic = (valueSuffix || "").toUpperCase().includes("GB");

  const { scaleFactor, suffix } = React.useMemo(() => {
    if (!isTraffic) {
      return { scaleFactor: 1, suffix: valueSuffix };
    }
    return {
      scaleFactor: 1024 * 1024 * 1024,
      suffix: " GB",
    };
  }, [isTraffic, valueSuffix]);

  useEffect(() => {
    const chartDom = chartRef.current;
    if (!chartDom || reversedData.length === 0) return;

    const chart = echarts.init(chartDom);
    chartInstance.current = chart;

    const values = reversedData.map((item) => item?.value ?? 0);
    const forMattedValues = values.map(v => isTraffic ? +(v / scaleFactor).toFixed(2) : parseFloat(v));
    const maxMBValue = Math.max(...forMattedValues) || 100;
    const max = valueSuffix === "%" ? 100 : Math.ceil(maxMBValue / 100) * 100;
    const interval = max / 5;

    const option = {
      grid: {
        top: 0,
        bottom: 0,
        left: 20,
        right: 10,
        containLabel: true,
      },
      tooltip: {
        trigger: "axis",
        axisPointer: {
          type: "shadow",
        },
        backgroundColor: "#fff",
        borderColor: "#e0e4f0",
        borderWidth: 1,
        textStyle: {
          color: "#333",
          fontSize: 12,
        },
        padding: [8, 12],
        extraCssText: CHART_TOOLTIP_STYLE,
        formatter: (params) => {
          const idx = params[0].dataIndex;
          const originalItem = reversedData[idx];
          if (!originalItem) return "";

          const value = isTraffic ? +((originalItem.value ?? 0) / scaleFactor).toFixed(2) : +Number(originalItem.value ?? 0).toFixed(2);
          const previousValue = originalItem.previousValue != null
            ? (isTraffic ? +((originalItem.previousValue ?? 0) / scaleFactor).toFixed(2) : +Number(originalItem.previousValue ?? 0).toFixed(2))
            : null;
          const rankBadgeValue = formatRankBadgeDisplay(originalItem?.rankBadge);

          return `
            <div style="font-weight: bold; margin-bottom: 4px; color: #172264;">${originalItem.name}</div>
            <div style="display: flex; justify-content: space-between; gap: 16px;">
              <span style="color: #707ca8;">Value:</span>
              <span style="font-weight: bold;">${value}${suffix}</span>
            </div>
            ${previousValue != null ?
              `
              <div style="display: flex; justify-content: space-between; gap: 16px; margin-top: 2px;">
                <span style="color: #707ca8;">Previous:</span>
                <span style="font-weight: bold;">${previousValue}${suffix}</span>
              </div>
              ` : ""
            }
            ${rankBadgeValue ?
              `
              <div style="display: flex; justify-content: space-between; gap: 16px; margin-top: 2px;">
                <span style="color: #707ca8;">Rank Change:</span>
                <span style="font-weight: bold;">${rankBadgeValue}</span>
              </div>
              ` : ""
            }
          `;
        },
      },
      xAxis: {
        type: "value",
        show: true,
        interval: interval,
        max: max,
        splitLine: {
          show: true,
          lineStyle: { color: 'rgba(0,0,0,0.08)' }
        },
        axisLabel: {
          show: true,
          fontSize: 11,
          color: "#888",
          formatter: (v) => v,
        },
        axisLine: { show: false },
        axisTick: { show: false },
      },
      yAxis: {
        type: "category",
        show: true,
        data: reversedData.map((item) => item.name),
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: { show: false },
        splitLine: {
          show: true,
          lineStyle: { color: 'rgba(0,0,0,0.08)' }
        },
      },
      series: [
        {
          type: "bar",
          data: forMattedValues,
          barWidth: "40%",
          barMaxWidth: 32,
          barCategoryGap: "20%",
          itemStyle: {
            color: "#707ca8",
          },
          label: {
            show: false
          },
        },
      ],
    };

    chart.setOption(option);

    const handleResize = () => chart.resize();
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      chart.dispose();
    };
  }, [reversedData, valueSuffix, scaleFactor, isTraffic]);

  if (data.length === 0) {
    return (
      <Card
        sx={{
          p: 2.5,
          borderRadius: "8px",
          border: "1px solid #e0e4f0",
          boxShadow: "0px 2px 8px rgba(0,0,0,0.04)",
          display: "flex",
          flexDirection: "column",
          height: "100%",
          minHeight: "220px",
        }}
      >
        <Typography sx={{ fontSize: "14px", color: "#172264", fontWeight: 600, mb: 2 }}>
          {title}
        </Typography>
        <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Typography sx={{ color: "#707ca8", fontSize: "14px" }}>No Data</Typography>
        </Box>
      </Card>
    );
  }

  return (
    <Card
      sx={{
        pt: 2.5,
        pb: 4,
        px: 2.5,
        borderRadius: "8px",
        border: "1px solid #e0e4f0",
        boxShadow: "0px 2px 8px rgba(0,0,0,0.04)",
        display: "flex",
        flexDirection: "column",
        height: "100%",
      }}
    >
      <Typography
        sx={{
          fontSize: "14px",
          color: "#172264",
          fontWeight: 600,
          mb: 2.5,
        }}
      >
        {title}
      </Typography>

      <Box sx={{ display: "flex", flexDirection: "row", width: "100%", height: "160px" }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            width: "125px",
            height: "100%",
            py: 0.5,
            pr: 1,
            boxSizing: "border-box",
          }}
        >
          {[...data].map((item, idx) => (
            <Box
              key={idx}
              sx={{
                display: "flex",
                alignItems: "center",
                width: "100%",
                minHeight: "22px",
                justifyContent: "space-between",
              }}
            >
              <Typography
                sx={{
                  fontSize: "12px",
                  color: "#172264",
                  fontWeight: 600,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  maxWidth: "75px",
                }}
              >
                {item.name}
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  width: "40px",
                }}
              >
                <TrendBadge rankChange={item.rankChange || item.rankBadge} />
              </Box>
            </Box>
          ))}
        </Box>
        <Box sx={{ flex: 1, height: "180px" }} ref={chartRef} />
      </Box>
    </Card>
  );
};

const RankBarCharts = ({ rankings = {} }) => {
  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={4}>
        <SingleRankChart
          title="Top 5 Switches by Traffic"
          data={rankings.topSwitchesByTraffic || []}
          valueSuffix=" GB"
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <SingleRankChart
          title="Top 5 Switch Ports by Traffic"
          data={rankings.topSwitchPortsByTraffic || []}
          valueSuffix=" GB"
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <SingleRankChart
          title="Top 5 Access Points by Traffic"
          data={rankings.topAccessPointsByTraffic || []}
          valueSuffix=" GB"
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <SingleRankChart
          title="Top 5 PoE Power Consumption"
          data={rankings.topPoePowerConsumption || []}
          valueSuffix=" W"
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <SingleRankChart
          title="Top 5 CPU by Usage"
          data={rankings.topCpuByUsage || []}
          valueSuffix="%"
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <SingleRankChart
          title="Top 5 Memory by Usage"
          data={rankings.topMemoryByUsage || []}
          valueSuffix="%"
        />
      </Grid>
    </Grid>
  );
};

export default RankBarCharts;
